import {observer} from "mobx-react";
import React, { useEffect } from "react";
import Drawer from "../../../components/drawer/drawer";
import  {FontBody2} from "../../../components/fonts/font-body2/font-body2";
import {BUTTON_VARIANTS} from "../../../components/inputs/button-variants";
import {Button} from "../../../components/inputs/button/button";
import ListLayout from "../../../components/layouts/list-layout/list-layout";
import {DefaultModalLayout} from "../../../components/modal/layouts/default-modal-layout";
import useDashboardTranslation from "../../../dashboard-translation/hooks/dashboard-translation-hook";
import useValidationGroup from "../../../hooks/validation-group/validation-group-hook";
import './add-item-drawer.scss';
import CreateRmaSettingsStore from "../stores/create-rma-settings-store";
import RequestProductStore from "../stores/request-product-store";
import AddItemDrawerLayout from "./add-item-drawer-layout";
import ItemPreview from "./item-preview";
import RmaItemFields from "./rma-item-fields";
import RmaItemSummary from "./rma-item-summary";
import RmaRequestStore from "../stores/rma-request-store";
import useTranslation from "../../../translation/hooks/translation-hook";
import Hr from "../../../components/hr/hr";

interface IAddItemDrawerProps{
	closeAction: ()=>void;
	requestProduct: RequestProductStore;
	request: RmaRequestStore;
    createRmaSettingsStore: CreateRmaSettingsStore;
}
const AddItemDrawer: React.FC<IAddItemDrawerProps> = observer(({closeAction, request, requestProduct, createRmaSettingsStore}) => {
	const vGroup = useValidationGroup();
	const isButtonDisabled = requestProduct.isAddButtonDisabled(vGroup.isValid());
    
    const { getTranslation, locale } = useDashboardTranslation();
    const {setSelectedLocale} = useTranslation();
    const pageScope = 'dashboard-create-rma';

    /** make sure all translations will be using the dashboard translations locale */
	useEffect(() => {
		setSelectedLocale(locale);
	},[locale, setSelectedLocale]);
    
	const handleAddClicked = () => {
        const conflictingResolutionLabel = request.getConflictingResolutionLabel(requestProduct.resolutionId!);
        if(conflictingResolutionLabel !== undefined){
            return requestProduct.showResolutionConflicts(conflictingResolutionLabel);
        }
		//I.e. first save
        const conflictingReturnMethodLabel = request.getConflictingReturnMethodLabel(requestProduct.returnMethodId!);
        if(conflictingReturnMethodLabel !== undefined){
            return requestProduct.showReturnMethodConflicts(conflictingReturnMethodLabel);
        }
		if(!requestProduct.editMode) {
			request.addProductStore(requestProduct);
		}
		else {
			request.saveProductStore(requestProduct);
		}
		closeAction();
	}
	
	return (
        <>
			<Drawer closeButton={{onClick:()=> {
				closeAction();
			}
			}}>
                <AddItemDrawerLayout
                    drawerBody={
                        <div className='add-item-drawer'>
                            <ListLayout>
                                <ItemPreview store={requestProduct} />
                                <Hr/>
                                <RmaItemFields store={requestProduct} createRmaSettingsStore={createRmaSettingsStore} vGroup={vGroup}/>
                                <RmaItemSummary store={requestProduct}/>
                            </ListLayout>
                        </div>
                    }
                    drawerFooter={
                        <div className="add-item-drawer">
                            
                            <Button 
                                disabled={isButtonDisabled}
                                onClick={handleAddClicked}
                                variant={BUTTON_VARIANTS.PrimaryAccent}
                                text={requestProduct.editMode ? getTranslation('SAVE', pageScope) : getTranslation('ADD', pageScope)}
                            />
                        </div>
                    }
                />
            </Drawer>
            
            { requestProduct.displayResolutionsConflicts &&
                <DefaultModalLayout isCloseDisabled={false} onCloseClicked={requestProduct.hideResolutionConflicts}>
                    <div className={'add-item-drawer-modal'}>
                        <FontBody2>
                            {getTranslation('RESOLUTION_CONFLICTS', pageScope, [requestProduct.conflictingResolution, requestProduct.resolutionLabel])}
                        </FontBody2>
                    </div>
                </DefaultModalLayout>
            }
            { requestProduct.displayReturnMethodConflicts &&
                <DefaultModalLayout isCloseDisabled={false} onCloseClicked={requestProduct.hideReturnMethodConflicts}>
                    <div className={'add-item-drawer-modal'}>
                        <FontBody2>
                            {getTranslation('RETURN_METHOD_CONFLICTS', pageScope, [requestProduct.conflictingReturnMethod, requestProduct.returnMethodLabel])}
                        </FontBody2>
                    </div>
                </DefaultModalLayout>
            }
        </>
	)
});

export default AddItemDrawer;